import { useState, useCallback, useMemo, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { isDarkOrLight } from '@hotjar/webapp/lib/isDarkOrLight';
import { tokens, PlanBadge, Radio, ColorPicker, Link, Stack, Field } from '@hotjar/hotsauce';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { css } from '@emotion/react';
import { useTranslation } from '@hotjar/translations';

const Colors = styled.div`
  display: grid;
  grid-template-columns: repeat(5, ${tokens.spacer.x4});
  column-gap: ${tokens.spacer.x1};
  row-gap: ${tokens.spacer.x1};
`;

export interface Color {
  fill: string;
  borderFill?: string;
  borderStyle?: 'solid' | 'dashed';
}

const ColorRadio = styled.input<Color>`
  cursor: pointer;
  padding: 0;
  appearance: none;
  position: relative;
  border-radius: 50%;

  /* && to increase specificity over insights.scss */
  && {
    margin: 0;
  }

  display: grid;
  place-content: center;

  &:hover {
    outline: 2px solid ${tokens.BRAND_COLORS.neutral_300} !important;
    outline-offset: 1px !important;
  }

  &:focus {
    outline: 2px solid ${tokens.color.focus} !important;
    outline-offset: 1px !important;
  }

  &::before {
    content: '';
    width: ${tokens.spacer.x4};
    height: ${tokens.spacer.x4};
    box-sizing: border-box;

    background: ${({ fill }) => fill};
    border-radius: 50%;
    ${({ borderFill, borderStyle = 'solid' }) =>
      borderFill !== undefined && `border: 1px ${borderStyle} ${borderFill};`}
  }
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin-top: ${tokens.spacer.x2};
  margin-bottom: ${tokens.spacer.x1};
`;
const ColorTileWrapper = styled.div`
  position: relative;
`;

const CheckIcon = styled.svg`
  display: inline-block;
  vertical-align: middle;
  position: absolute;
  left: 10px;
  top: 11px;
  pointer-events: none;
`;

const CustomRadio = styled(Radio)`
  margin-top: ${tokens.spacer.x1};
  margin-bottom: 0;

  & input[type='radio'] {
    margin: 0;
  }
`;

const StyledColorPicker = styled(ColorPicker)`
  margin-left: ${tokens.spacer.x3};
`;

const ResetButton = styled(Link)`
  color: ${tokens.color.text_default};
  flex-shrink: 0;
`;

const StyledStack = styled(Stack)`
  align-items: start;
  margin-top: ${tokens.spacer.x1};
`;

const StyledField = styled(Field)`
  > label {
    margin-bottom: ${tokens.spacer.x2};
  }
`;

const ColorTile = ({
  color,
  isChecked,
  onChange,
  onPreviewColor,
}: {
  color: Color;
  isChecked: boolean;
  onChange: (color: string) => void;
  onPreviewColor: (color: string | null) => void;
}) => {
  const { t } = useTranslation('feedback', { keyPrefix: 'wizard.section.appearance.color' });
  return (
    <ColorTileWrapper>
      <ColorRadio
        name="color"
        onChange={() => onChange(color.fill)}
        title={`${isChecked ? t('selected-color') : t('pick-color')} ${color.fill}`}
        fill={color.fill}
        value={color.fill}
        borderFill={color.borderFill}
        type="radio"
        checked={isChecked}
        onMouseEnter={() => onPreviewColor(color.fill)}
        onMouseLeave={() => onPreviewColor(null)}
        onFocus={() => onPreviewColor(color.fill)}
        onBlur={() => onPreviewColor(null)}
      />
      {isChecked && (
        <CheckIcon
          width="11"
          height="11"
          viewBox="0 0 914.2857142857143 1024"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M900 305.143q0 22.857-16 38.857l-491.429 491.429q-16 16-38.857 16t-38.857-16l-284.571-284.571q-16-16-16-38.857t16-38.857l77.714-77.714q16-16 38.857-16t38.857 16l168 168.571 374.857-375.429q16-16 38.857-16t38.857 16l77.714 77.714q16 16 16 38.857z"
            fill={isDarkOrLight(color.fill) === 'light' ? '#000000' : tokens.color.white}
          />
        </CheckIcon>
      )}
    </ColorTileWrapper>
  );
};

export interface ColorPaletteProps {
  label: string;
  showColorPalette?: boolean;
  defaultColor: string;
  additionalBaseColor?: string;
  baseColors: Color[];
  advancedColors?: Color[];
  canUseAdvancedColors?: boolean;
  advancedColorsPlan?: FeatureGroup;
  onUnavailableColorSelected?: () => void;
  value: string;
  onChange: (result: { color: string; isCustom: boolean }) => void;
  allowCustom?: boolean;
  onPreviewColor: (color: string | null) => void;
}

function ensureDefaultColorIsFirst(presetColors: Color[], defaultColor: string): Color[] {
  const defaultColorIndex = presetColors.findIndex((c) => c.fill === defaultColor);
  if (defaultColorIndex > 0) {
    return [
      presetColors[defaultColorIndex],
      ...presetColors.slice(0, defaultColorIndex),
      ...presetColors.slice(defaultColorIndex + 1),
    ];
  }

  return presetColors;
}

export function ColorPalette(props: ColorPaletteProps): JSX.Element | null {
  const {
    defaultColor,
    baseColors,
    additionalBaseColor,
    advancedColors,
    canUseAdvancedColors = true,
    advancedColorsPlan,
    allowCustom = false,
    value,
    label,
    showColorPalette = true,
    onChange,
    onUnavailableColorSelected,
    onPreviewColor,
  } = props;
  const { t } = useTranslation('feedback', { keyPrefix: 'wizard.section.appearance' });
  const presetColors = useMemo(
    () =>
      ensureDefaultColorIsFirst(
        [
          ...baseColors,
          ...(advancedColors || []),
          ...(additionalBaseColor !== undefined ? [{ fill: additionalBaseColor }] : []),
        ],
        defaultColor,
      ),
    [baseColors, advancedColors, defaultColor, additionalBaseColor],
  );

  const isPredefinedColor = useCallback(
    (color: string) => presetColors.some((c) => c.fill === color),
    [presetColors],
  );

  const isBaseColor = useCallback(
    (color: string) => baseColors.some((c) => c.fill === color),
    [baseColors],
  );

  const [isCustomModeSelected, setCustomModeSelected] = useState(!isPredefinedColor(value));
  const [customColor, setCustomColor] = useState<string | null>(
    isPredefinedColor(value) ? null : value,
  );

  useLayoutEffect(() => {
    if (isPredefinedColor(value)) {
      return;
    }

    setCustomModeSelected(true);
    setCustomColor(value);
  }, [isPredefinedColor, value]);

  function setColor(color: string) {
    if (!canUseAdvancedColors && !isBaseColor(color) && color !== additionalBaseColor) {
      onUnavailableColorSelected?.();
      return;
    }

    const isPredefined = isPredefinedColor(color);
    if (!isPredefined) {
      setCustomColor(color);
      setCustomModeSelected(true);
    }

    onChange({ color, isCustom: !isPredefined });
  }

  function resetColor() {
    setCustomModeSelected(false);
    setColor(defaultColor);
  }

  function selectPredefined(color: string) {
    setCustomModeSelected(false);
    setColor(color);
  }

  function selectCustomMode() {
    if (!canUseAdvancedColors) {
      onUnavailableColorSelected?.();
      return;
    }

    setCustomModeSelected(true);
    setColor(customColor ?? value);
  }

  const showResetButton = value !== defaultColor && !isCustomModeSelected;

  return showColorPalette ? (
    <StyledField inputId="color-palette" labelText={label} size="l">
      <form onSubmit={(e) => e.preventDefault()}>
        {(canUseAdvancedColors || !advancedColors) && (
          <Colors>
            {presetColors.map((color) => (
              <ColorTile
                color={color}
                key={color.fill}
                onChange={() => selectPredefined(color.fill)}
                isChecked={value === color.fill && !isCustomModeSelected}
                onPreviewColor={onPreviewColor}
              />
            ))}
          </Colors>
        )}
        {!canUseAdvancedColors && advancedColors && onUnavailableColorSelected && (
          <>
            <Colors>
              {baseColors.map((color) => (
                <ColorTile
                  color={color}
                  key={color.fill}
                  isChecked={value === color.fill && !isCustomModeSelected}
                  onChange={() => selectPredefined(color.fill)}
                  onPreviewColor={onPreviewColor}
                />
              ))}
              {additionalBaseColor !== undefined && (
                <ColorTile
                  color={{ fill: additionalBaseColor }}
                  isChecked={value === additionalBaseColor && !isCustomModeSelected}
                  onChange={() => selectPredefined(additionalBaseColor)}
                  onPreviewColor={onPreviewColor}
                />
              )}
            </Colors>
            {advancedColorsPlan && (
              <StyledPlanBadge
                kind={advancedColorsPlan}
                size="s"
                onClick={onUnavailableColorSelected}
              />
            )}
            <Colors>
              {advancedColors.map((color) => (
                <ColorTile
                  color={color}
                  key={color.fill}
                  onChange={() => selectPredefined(color.fill)}
                  isChecked={value === color.fill && !isCustomModeSelected}
                  onPreviewColor={onPreviewColor}
                />
              ))}
            </Colors>
          </>
        )}
        <StyledStack direction="column" spacing="x1">
          {allowCustom && (
            <>
              <CustomRadio
                name="color"
                onChange={selectCustomMode}
                labelContent={t('color-palette.custom')}
                isChecked={isCustomModeSelected}
              />
              {isCustomModeSelected && (
                <Stack
                  onMouseEnter={() => onPreviewColor(customColor ?? value)}
                  onMouseLeave={() => onPreviewColor(null)}
                  onFocus={() => onPreviewColor(customColor ?? value)}
                  onBlur={() => onPreviewColor(null)}
                >
                  <StyledColorPicker
                    css={css`
                      width: calc(${tokens.spacer.x1} * 17);
                    `}
                    value={customColor ?? value}
                    onChange={setColor}
                    title={t('color-palette.custom-hex-value')}
                  />
                  <ResetButton as="button" type="button" kind="inline" onClick={resetColor}>
                    {t('label.reset')}
                  </ResetButton>
                </Stack>
              )}
            </>
          )}

          {showResetButton && (
            <ResetButton as="button" type="button" kind="inline" onClick={resetColor}>
              {t('label.reset')}
            </ResetButton>
          )}
        </StyledStack>
      </form>
    </StyledField>
  ) : null;
}
